// DsIconButton — implementa a classe `.iconbtn` do canvas (DESIGN-SYSTEM.md §6.2).
//
// O `IconButton` do MUI é redondo, sem borda e com `padding:9px` (área de ~38px); o canvas
// não tem nenhum botão-ícone redondo. Por isso a base aqui é `ButtonBase` com a caixa
// 30×30 de raio 8 do canvas.
//
// Usar SÓ onde o canvas realmente usa `.iconbtn`. Nos demais casos o canvas quer botão
// rotulado — `DsButton tone="neutral"`.
//
// ----------------------------------------------------------------------
// RODADA DE CONSERTO DA CAMADA COMPARTILHADA — DECISOES-PENDENTES.md §3.4
// Pedido por 8 LOTES (L08, L12, L13, L16, L17, L18, L22, L25).
//
// Três defeitos conferidos no arquivo antes de mexer:
//   1. `onClick?: () => void` não recebia o evento. Numa grade com linha clicável não
//      dava para chamar `stopPropagation`, e clicar em "excluir" abria o detalhe da
//      linha junto (L17 K2). Agora é `(event) => void`. A mudança é ADITIVA: todo
//      `() => void` que já existe continua atribuível.
//   2. O ícone estava cravado em 15px. O canvas só fixa 15 na regra base
//      (`.iconbtn svg{width:15px;height:15px}`) e sobrescreve inline nas ações de
//      linha (18px) e nos glifos grandes. Agora `iconSize` abre isso, com 15 de
//      padrão — nenhuma chamada existente muda de tamanho.
//   3. Não havia tom de perigo. O canvas tem a regra literal
//      `.iconbtn.danger{color:var(--error-main)}` (arrecadacao, nota-nfse), aplicada
//      sobre a variante SEM borda. `tone="danger"` é exatamente isso.
// Somado a isso, `sx` passou a existir (a spec §3.9 trata composição de `sx`) e o
// componente virou `forwardRef`, porque o botão de `mais-opcoes` é âncora de `<Menu>`
// em ~20 arquivos (§3.3) e cada um estava embrulhando num `<span ref>`.
//
// A união de tons continua FECHADA (§1.2): não há passthrough de `color`/`size` do MUI.

import type { Ref, MouseEvent } from 'react';
import type { Theme, SxProps } from '@mui/material/styles';
import type { SystemStyleObject } from '@mui/system';

import { forwardRef } from 'react';

import ButtonBase from '@mui/material/ButtonBase';

import { DsIcon } from './DsIcon';
import { DS_BOX_RADIUS } from './tokens';

import type { DsIconName, DsIconSize } from './DsIcon';

// ----------------------------------------------------------------------

export type DsIconButtonProps = {
  icon: DsIconName;
  /**
   * `neutral` (padrão) tem borda de 1px; `bare` é o X de fechar do modal, sem borda;
   * `danger` é o `bare` com o traço em `error.main` — a regra `.iconbtn.danger` do canvas.
   */
  tone?: 'neutral' | 'bare' | 'danger';
  /** Tamanho do glifo. Padrão: 15 — a regra base `.iconbtn svg` do canvas. */
  iconSize?: DsIconSize;
  /** Obrigatório: o botão não tem rótulo visível. */
  'aria-label': string;
  /** Recebe o evento — dá `stopPropagation` em grade de linha clicável. */
  onClick?: (event: MouseEvent<HTMLButtonElement>) => void;
  disabled?: boolean;
  /** Dica nativa; o `aria-label` continua sendo o nome acessível. */
  title?: string;
  /** Padrão: 'button' — não submete formulário por acidente. */
  type?: 'button' | 'submit';
  /** Atributos de menu (`aria-haspopup`, `aria-expanded`, `aria-controls`) quando é âncora. */
  'aria-haspopup'?: boolean;
  'aria-expanded'?: boolean;
  'aria-controls'?: string;
  sx?: SxProps<Theme>;
};

// ----------------------------------------------------------------------

/** Caixa comum: 30×30, grade centrada, raio 8. */
const SX_BASE = {
  width: '30px',
  height: '30px',
  flexShrink: 0,
  display: 'inline-grid',
  placeItems: 'center',
  borderRadius: `${DS_BOX_RADIUS}px`,
  '&.Mui-disabled': { color: 'text.disabled' },
} satisfies SystemStyleObject<Theme>;

/** `.iconbtn` padrão: traço secundário com borda de 1px. */
const SX_NEUTRAL = {
  color: 'text.secondary',
  border: '1px solid',
  borderColor: 'divider',
} satisfies SystemStyleObject<Theme>;

/** `bare`: o X de fechar do modal — mesma caixa, sem borda. */
const SX_BARE = {
  color: 'action.active',
  border: 0,
} satisfies SystemStyleObject<Theme>;

/** `danger`: `.iconbtn.danger{color:var(--error-main)}` — sem borda, traço de erro. */
const SX_DANGER = {
  color: 'error.main',
  border: 0,
} satisfies SystemStyleObject<Theme>;

const SX_POR_TOM = {
  neutral: SX_NEUTRAL,
  bare: SX_BARE,
  danger: SX_DANGER,
} as const;

// ----------------------------------------------------------------------

function DsIconButtonRaiz(
  {
    icon,
    tone = 'neutral',
    iconSize = 15,
    'aria-label': ariaLabel,
    onClick,
    disabled,
    title,
    type = 'button',
    sx,
    ...acessibilidade
  }: DsIconButtonProps,
  ref: Ref<HTMLButtonElement>
) {
  return (
    <ButtonBase
      ref={ref}
      type={type}
      aria-label={ariaLabel}
      title={title}
      onClick={onClick}
      disabled={disabled}
      sx={[SX_BASE, SX_POR_TOM[tone], ...(Array.isArray(sx) ? sx : [sx])]}
      {...acessibilidade}
    >
      <DsIcon name={icon} size={iconSize} />
    </ButtonBase>
  );
}

export const DsIconButton = forwardRef(DsIconButtonRaiz);

DsIconButton.displayName = 'DsIconButton';
